// Helpers de formatação para o Mapa 2026.
// Lida com nomes "sujos" vindos do Voalle (BMI/HORIZON/WFREDES/TANTO/IMPLANTAR/...
// têm sufixos `_zone_NEUTRA-XXX_authd_YYYYMMDD`, descrições com `_descr_...`,
// etc.) e expõe utilitários de aging legível.

// Regex zone tags (parceiros / segmentos comerciais que aparecem em descrições).
// Captura tudo que vier depois de `_zone_` ou `@` até o próximo separador.
const ZONE_RE = /_zone_([A-Z][\w-]+)/i;
const AUTHD_RE = /_authd_(\d{6,8})/i;
const DESCR_RE = /_descr_/i;
const PARTNER_RE = /^(HORIZON|BMI|TANTO|WFREDES|R2|SPACE|IMPLANTAR)[@-]/i;

// Limites razoáveis para apresentação (evita explodir layout).
const NAME_LIMIT = 60;

/**
 * Transforma uma string de descrição/nome Voalle "suja" em campos limpos.
 * Exemplos do mart bi.dim_voalle_customer:
 *   "Paulo Roberto de Jesus"
 *     → { display: "Paulo Roberto de Jesus", subtitle: null, zone: null, partner: null }
 *
 *   "BMI-ID45451-DEBORASTEFANIDASILVASANTOS-CTO05RT03C37-PORTA1_zone_NEUTRA-BMI_authd_20250612"
 *     → { display: "Debora Stefani da Silva Santos", subtitle: "BMI · CTO05 RT03 C37 P1", zone: "NEUTRA-BMI", partner: "BMI" }
 *
 *   "HORIZON@Vanessa Barbosa da Silva_00019_BHZ-C0019-RT04-CT02-16_zone_NEUTRA-HORIZON_authd_20260331"
 *     → { display: "Vanessa Barbosa da Silva", subtitle: "HORIZON · C0019 RT04 CT02-16", zone: "NEUTRA-HORIZON", partner: "HORIZON" }
 */
export function parseName(raw) {
  if (!raw || typeof raw !== 'string') {
    return { display: '', subtitle: null, zone: null, partner: null, original: raw };
  }
  const original = raw;

  const zoneMatch = raw.match(ZONE_RE);
  const zone = zoneMatch?.[1] || null;

  let working = raw;
  // Strip do tail `_zone_...`, `_authd_...`, `_descr_...`.
  working = working.split(ZONE_RE)[0];
  working = working.split(AUTHD_RE)[0];
  working = working.split(DESCR_RE)[0];

  // Detectar partner (HORIZON@..., BMI-..., TANTO@...)
  let partner = null;
  const partnerMatch = working.match(PARTNER_RE);
  if (partnerMatch) {
    partner = partnerMatch[1].toUpperCase();
  } else if (zone) {
    // partner pode estar só na zone (ex: NEUTRA-HORIZON → HORIZON)
    const z = zone.match(/^NEUTRA-(\w+)/i);
    if (z) partner = z[1].toUpperCase();
  }

  let body = working;
  if (partner) {
    // remove prefixo do partner: "HORIZON@Vanessa..." → "Vanessa..."
    body = body.replace(PARTNER_RE, '');
  }

  // Pega só o "nome humano" — corta no primeiro `_` ou `-` que vem depois de palavras
  // (rótulos tipo `_00019_BHZ-C0019` ou `-CTO05RT03`).
  // Heurística: tudo até o primeiro grupo que contém número >= 3 dígitos
  //   ou keyword tipo CTO/CT/RT/POP/odb/zone.
  const nameOnly = extractHumanName(body);

  // Estrutura do CTO/RT (opcional na subtitle)
  const ctoLabel = extractCtoLabel(working);

  let subtitle = null;
  if (partner && ctoLabel) {
    subtitle = `${partner} · ${ctoLabel}`;
  } else if (partner) {
    subtitle = partner;
  } else if (ctoLabel) {
    subtitle = ctoLabel;
  } else if (zone && zone !== `NEUTRA-${partner}`) {
    subtitle = zone;
  }

  let display = nameOnly || stripUnderscores(body) || raw;
  display = titleCaseIfShouty(display);
  if (display.length > NAME_LIMIT) display = `${display.slice(0, NAME_LIMIT - 1)}…`;

  return { display, subtitle, zone, partner, original };
}

function extractHumanName(s) {
  if (!s) return '';
  // Procura o primeiro segmento "nome" — sequência de palavras separadas por
  // espaço ou underscore, ANTES de chegar em rótulos técnicos.
  // Pega até o primeiro `_<digit3>` ou `_descr` ou keyword (CTO|RT|CT|BHZ|FPC|odb|extid|POP|fpc|olt|zone)
  const stopRe = /(^|[_\s-])(\d{3,}|[a-z]+-?C\d|RT\d|CT\d|BHZ|FPC?\d|odb|extid|authd|olt|zone|POP-|ID\d|C\d{4}|CTO|PORTA)/i;
  const m = s.match(stopRe);
  let raw = m ? s.slice(0, m.index) : s;
  raw = raw.replace(/^[\s_@:-]+|[\s_@:-]+$/g, '');
  // Converte underscores remanescentes em espaços (ex: "JOAO_DA_SILVA" → "JOAO DA SILVA")
  raw = raw.replace(/_+/g, ' ').trim();
  if (raw.length < 3) return ''; // nada útil
  return raw;
}

function extractCtoLabel(s) {
  // Capturas comuns:
  //   BHZ-C0019-RT04-CT02-16   → "C0019 RT04 CT02-16"
  //   FPC42-RT05-CT07-P05      → "FPC42 RT05 CT07 P05"
  //   CTO05RT03C37             → "CTO05 RT03 C37"
  //   C26-RT1-CT5---LC6037     → "C26 RT1 CT5"
  if (!s) return null;
  const blob = s.match(/((C|CTO|CT|RT|FPC?|BHZ-?C)\d+[\w-]*)+/i);
  if (!blob) return null;
  return blob[0]
    .replace(/^BHZ-?/i, '')
    .replace(/[-_]+/g, ' ')
    .replace(/\s+/g, ' ')
    .trim()
    .slice(0, 40);
}

function stripUnderscores(s) {
  return (s || '').replace(/_+/g, ' ').replace(/\s+/g, ' ').trim();
}

function titleCaseIfShouty(s) {
  if (!s) return s;
  // Se >= 80% das letras são maiúsculas, faz Title Case (preservando "DE", "DA", "DO", etc.)
  const letters = s.replace(/[^a-zA-Z]/g, '');
  if (!letters) return s;
  const upper = letters.replace(/[^A-Z]/g, '');
  if (upper.length / letters.length < 0.8) return s; // mistura natural — manter

  const SMALL = new Set(['de', 'da', 'do', 'dos', 'das', 'e', 'di', 'du']);
  return s
    .toLowerCase()
    .split(/(\s+)/)
    .map((w, i) => {
      if (/^\s+$/.test(w)) return w;
      if (SMALL.has(w) && i > 0) return w;
      return w.charAt(0).toUpperCase() + w.slice(1);
    })
    .join('');
}

/**
 * Minutos desde uma timestamp ISO. Retorna `null` se inválido ou futuro.
 */
export function agingMinutes(iso) {
  if (!iso) return null;
  const t = new Date(iso).getTime();
  if (!Number.isFinite(t)) return null;
  const m = Math.floor((Date.now() - t) / 60000);
  return m < 0 ? null : m;
}

/**
 * Categoria de aging — usada para colorir o "dot" da coluna.
 *   <5min  → fresh (verde)
 *   <30min → recent (amarelo)
 *   <1h    → warning (laranja)
 *   <6h    → bad (vermelho)
 *   >=6h   → critical (vermelho escuro)
 */
export function agingBucket(min) {
  if (min == null) return 'unknown';
  if (min < 5) return 'fresh';
  if (min < 30) return 'recent';
  if (min < 60) return 'warning';
  if (min < 360) return 'bad';
  return 'critical';
}

export const AGING_COLOR = {
  unknown: '#9ca3af',
  fresh: '#10b981',
  recent: '#84cc16',
  warning: '#f59e0b',
  bad: '#ef4444',
  critical: '#991b1b',
};

/**
 * Formata aging para display compacto.
 *   5    → "5m"
 *   85   → "1h25"
 *   725  → "12h05"
 *   1500 → "1d 1h"
 *   8640 → "6d"
 */
export function formatAging(min) {
  if (min == null) return '—';
  if (min < 1) return 'agora';
  if (min < 60) return `${min}m`;
  const h = Math.floor(min / 60);
  const remM = min % 60;
  if (h < 24) return remM ? `${h}h${String(remM).padStart(2, '0')}` : `${h}h`;
  const d = Math.floor(h / 24);
  const remH = h % 24;
  return remH ? `${d}d ${remH}h` : `${d}d`;
}

/**
 * Pré-sets do componente AgingSelect.
 * value = min_aging_min no backend.
 */
export const AGING_PRESETS = [
  { value: 0, label: 'Qualquer idade' },
  { value: 5, label: '≥ 5 min' },
  { value: 30, label: '≥ 30 min' },
  { value: 60, label: '≥ 1 h' },
  { value: 360, label: '≥ 6 h' },
  { value: 1440, label: '≥ 24 h' },
  { value: 10080, label: '≥ 7 d' },
];
